/** Generator gambar kartu pengenal calon peserta (Canvas → PNG). */

import QRCode from "qrcode";
import { getTenantHost } from "../services/api";

export const KARTU_FORMAT = {
  HORIZONTAL: "horizontal",
  VERTICAL: "vertical",
};

const CARD_H_W = 1012;
const CARD_H_H = 638;
const CARD_V_W = 638;
const CARD_V_H = 1012;

const loadImageViaFetch = async (src) => {
  const tenantHost = getTenantHost();
  const headers = tenantHost ? { "X-Tenant-Host": tenantHost } : {};
  const resp = await fetch(src, { mode: "cors", credentials: "omit", headers });
  if (!resp.ok) {
    throw new Error(`HTTP ${resp.status}`);
  }
  const contentType = resp.headers.get("content-type") || "";
  if (!contentType.startsWith("image/")) {
    throw new Error("Respon bukan gambar");
  }
  const blob = await resp.blob();
  const objUrl = URL.createObjectURL(blob);
  try {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(objUrl);
        resolve(img);
      };
      img.onerror = () => {
        URL.revokeObjectURL(objUrl);
        reject(new Error(`Gagal decode gambar: ${src}`));
      };
      img.src = objUrl;
    });
  } catch (err) {
    URL.revokeObjectURL(objUrl);
    throw err;
  }
};

const loadImageDirect = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Gagal memuat gambar: ${src}`));
    img.src = src;
  });

const loadImage = async (src) => {
  if (!src) {
    throw new Error("No image src");
  }
  try {
    return await loadImageViaFetch(src);
  } catch (_) {
    return loadImageDirect(src);
  }
};

const roundRect = (ctx, x, y, w, h, r) => {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
  ctx.lineTo(x + radius, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
};

const drawImageCover = (ctx, img, x, y, w, h) => {
  const scale = Math.max(w / img.width, h / img.height);
  const sw = img.width * scale;
  const sh = img.height * scale;
  const sx = x + (w - sw) / 2;
  const sy = y + (h - sh) / 2;
  ctx.drawImage(img, sx, sy, sw, sh);
};

const truncateText = (ctx, text, maxWidth) => {
  if (!text) return "";
  if (ctx.measureText(text).width <= maxWidth) return text;
  let out = text;
  while (out.length > 1 && ctx.measureText(`${out}…`).width > maxWidth) {
    out = out.slice(0, -1);
  }
  return `${out}…`;
};

const hexToRgb = (hex) => {
  const h = (hex || "#10b981").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};

const mixColor = (hex, amount, toward = 255) => {
  const { r, g, b } = hexToRgb(hex);
  const mix = (c) => Math.round(c + (toward - c) * amount);
  return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`;
};

const formatTgl = (value) => {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch (_) {
    return String(value);
  }
};

const normalizeStatus = (status) => (status || "").toUpperCase().trim();

const isCalonOrPelatihan = (status) => {
  const s = normalizeStatus(status);
  return s === "CALON PESERTA" || s === "PESERTA PELATIHAN";
};

const getKartuTitle = (status) =>
  normalizeStatus(status) === "DISETUJUI"
    ? "KARTU PENDAMPING PROSES PRODUK HALAL"
    : "KARTU CALON PENDAMPING PROSES PRODUK HALAL";

const resolveNoRegister = (status, noRegister) => {
  if (isCalonOrPelatihan(status)) return "-";
  return (noRegister || "").trim() || "-";
};

export { resolveNoRegister };

const drawPhotoPlaceholder = (ctx, x, y, w, h, circular = false) => {
  if (circular) {
    ctx.fillStyle = "#e5e7eb";
    ctx.beginPath();
    ctx.arc(x + w / 2, y + h / 2, w / 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#9ca3af";
    ctx.beginPath();
    ctx.arc(x + w / 2, y + h * 0.38, w * 0.16, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.ellipse(x + w / 2, y + h * 0.72, w * 0.26, h * 0.14, 0, 0, Math.PI * 2);
    ctx.fill();
    return;
  }
  ctx.fillStyle = "#e5e7eb";
  roundRect(ctx, x, y, w, h, 12);
  ctx.fill();
  ctx.fillStyle = "#9ca3af";
  ctx.beginPath();
  ctx.arc(x + w / 2, y + h * 0.38, w * 0.16, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(x + w / 2, y + h * 0.78, w * 0.28, h * 0.18, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#6b7280";
  ctx.font = "600 14px Arial, sans-serif";
  ctx.textAlign = "center";
  ctx.fillText("FOTO", x + w / 2, y + h - 16);
  ctx.textAlign = "left";
};

const drawCircularPhoto = async (ctx, cx, cy, radius, fotoUrl) => {
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, radius + 6, 0, Math.PI * 2);
  ctx.fillStyle = "#ffffff";
  ctx.fill();
  ctx.strokeStyle = mixColor("#ffffff", -0.2, 200);
  ctx.lineWidth = 4;
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.clip();

  if (fotoUrl) {
    try {
      const photo = await loadImage(fotoUrl);
      drawImageCover(ctx, photo, cx - radius, cy - radius, radius * 2, radius * 2);
    } catch (_) {
      drawPhotoPlaceholder(ctx, cx - radius, cy - radius, radius * 2, radius * 2, true);
    }
  } else {
    drawPhotoPlaceholder(ctx, cx - radius, cy - radius, radius * 2, radius * 2, true);
  }
  ctx.restore();
};

const drawHalalBadge = (ctx, cx, cy, color) => {
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, 42, 0, Math.PI * 2);
  ctx.fillStyle = "#ffffff";
  ctx.fill();
  ctx.strokeStyle = color;
  ctx.lineWidth = 3;
  ctx.stroke();
  ctx.fillStyle = color;
  ctx.font = "bold 13px Arial, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("HALAL", cx, cy - 6);
  ctx.font = "10px Arial, sans-serif";
  ctx.fillText("CERTIFIED", cx, cy + 10);
  ctx.restore();
};

const drawInfoRow = (ctx, x, y, label, value, maxWidth) => {
  ctx.fillStyle = "#6b7280";
  ctx.font = "600 16px Arial, sans-serif";
  ctx.fillText(label.toUpperCase(), x, y);
  ctx.fillStyle = "#111827";
  ctx.font = "bold 26px Arial, sans-serif";
  const lineY = y + 34;
  ctx.fillText(truncateText(ctx, value || "-", maxWidth), x, lineY);
  return lineY + 28;
};

const drawLanyardSlots = (ctx, cx, y, slotColor) => {
  ctx.fillStyle = slotColor || "rgba(255,255,255,0.35)";
  [-22, 22].forEach((offset) => {
    roundRect(ctx, cx + offset - 18, y, 36, 12, 5);
    ctx.fill();
    ctx.fillStyle = "rgba(255,255,255,0.15)";
    roundRect(ctx, cx + offset - 14, y + 2, 28, 8, 4);
    ctx.fill();
    ctx.fillStyle = slotColor || "rgba(255,255,255,0.35)";
  });
};

const drawVerticalThemeGradient = (ctx, w, h, primaryColor, primaryDark, primaryLight) => {
  const light = primaryLight || mixColor(primaryColor, 0.45);
  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, primaryDark);
  grad.addColorStop(0.45, primaryColor);
  grad.addColorStop(1, light);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);
};

const drawQrCode = async (ctx, text, cx, y, size) => {
  const value = (text || "").trim();
  if (!value) return false;
  try {
    const qrCanvas = document.createElement("canvas");
    await QRCode.toCanvas(qrCanvas, value, {
      width: size,
      margin: 1,
      errorCorrectionLevel: "M",
      color: { dark: "#111827", light: "#ffffff" },
    });
    ctx.drawImage(qrCanvas, cx - size / 2, y, size, size);
    return true;
  } catch (_) {
    return false;
  }
};

const drawVerticalDetailRow = (ctx, x, y, w, label, value) => {
  ctx.strokeStyle = "#e5e7eb";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x, y + 36);
  ctx.lineTo(x + w, y + 36);
  ctx.stroke();

  ctx.fillStyle = "#6b7280";
  ctx.font = "600 13px Arial, sans-serif";
  ctx.textAlign = "left";
  ctx.fillText(label, x, y + 16);

  ctx.fillStyle = "#111827";
  ctx.font = "600 18px Arial, sans-serif";
  ctx.fillText(truncateText(ctx, value || "-", w - 8), x, y + 34);
  return y + 44;
};

const wrapText = (ctx, text, maxWidth) => {
  if (!text) return [];
  const words = String(text).trim().split(/\s+/);
  const lines = [];
  let line = "";
  words.forEach((word) => {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width <= maxWidth) {
      line = test;
    } else {
      if (line) lines.push(line);
      line = word;
    }
  });
  if (line) lines.push(line);
  return lines;
};

const drawCenteredLines = (ctx, lines, cx, y, lineHeight, maxLines = 99) => {
  const shown = lines.slice(0, maxLines);
  shown.forEach((line, i) => {
    ctx.fillText(line, cx, y + i * lineHeight);
  });
  return y + shown.length * lineHeight + 4;
};

async function drawLogoCentered(ctx, logoUrl, cx, y, maxH, maxW) {
  if (!logoUrl) return 0;
  try {
    const logo = await loadImage(logoUrl);
    const logoH = maxH;
    const logoW = Math.min(maxW, (logo.width / logo.height) * logoH);
    ctx.drawImage(logo, cx - logoW / 2, y, logoW, logoH);
    return logoH;
  } catch (_) {
    return 0;
  }
}

async function drawLogo(ctx, logoUrl, x, y, maxH, maxW) {
  if (!logoUrl) return false;
  try {
    const logo = await loadImage(logoUrl);
    const logoH = maxH;
    const logoW = Math.min(maxW, (logo.width / logo.height) * logoH);
    ctx.drawImage(logo, x, y, logoW, logoH);
    return true;
  } catch (_) {
    return false;
  }
}

/** Kartu landscape — ukuran standar CR80 (85,6 × 54 mm). */
async function generateKartuHorizontal(opts) {
  const {
    nama = "",
    nik = "",
    status = "CALON PESERTA",
    kodeReferral = "",
    noRegister = "",
    idPendamping = "",
    fotoUrl = "",
    logoUrl = "",
    orgName = "LP3H",
    orgAlamat = "",
    orgTelepon = "",
    primaryColor = "#10b981",
    primaryDark = "#047857",
    kabupatenNama = "",
    provinsiNama = "",
  } = opts;

  const canvas = document.createElement("canvas");
  canvas.width = CARD_H_W;
  canvas.height = CARD_H_H;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, CARD_H_W, CARD_H_H);
  ctx.shadowColor = "rgba(0,0,0,0.12)";
  ctx.shadowBlur = 24;
  ctx.shadowOffsetY = 8;
  roundRect(ctx, 12, 12, CARD_H_W - 24, CARD_H_H - 24, 20);
  ctx.fillStyle = "#ffffff";
  ctx.fill();
  ctx.shadowColor = "transparent";

  ctx.save();
  roundRect(ctx, 12, 12, CARD_H_W - 24, 118, 20);
  ctx.clip();
  const headerGrad = ctx.createLinearGradient(0, 0, CARD_H_W, 0);
  headerGrad.addColorStop(0, primaryColor);
  headerGrad.addColorStop(1, primaryDark);
  ctx.fillStyle = headerGrad;
  ctx.fillRect(12, 12, CARD_H_W - 24, 118);
  ctx.restore();

  let headerTextX = 40;
  const hasLogo = await drawLogo(ctx, logoUrl, 36, 28, 52, 52);
  if (hasLogo) headerTextX = 100;

  ctx.fillStyle = "#ffffff";
  ctx.font = "bold 30px Arial, Helvetica, sans-serif";
  ctx.textAlign = "left";
  ctx.fillText(truncateText(ctx, orgName, CARD_H_W - headerTextX - 160), headerTextX, 56);
  ctx.font = "600 15px Arial, Helvetica, sans-serif";
  ctx.globalAlpha = 0.92;
  ctx.fillText(getKartuTitle(status), headerTextX, 88);
  ctx.globalAlpha = 1;
  drawHalalBadge(ctx, CARD_H_W - 72, 72, primaryColor);

  const photoX = 48;
  const photoY = 155;
  const photoW = 210;
  const photoH = 260;
  ctx.strokeStyle = primaryColor;
  ctx.lineWidth = 3;
  roundRect(ctx, photoX, photoY, photoW, photoH, 14);
  ctx.stroke();

  if (fotoUrl) {
    try {
      const photo = await loadImage(fotoUrl);
      ctx.save();
      roundRect(ctx, photoX + 3, photoY + 3, photoW - 6, photoH - 6, 12);
      ctx.clip();
      drawImageCover(ctx, photo, photoX + 3, photoY + 3, photoW - 6, photoH - 6);
      ctx.restore();
    } catch (_) {
      drawPhotoPlaceholder(ctx, photoX + 3, photoY + 3, photoW - 6, photoH - 6);
    }
  } else {
    drawPhotoPlaceholder(ctx, photoX + 3, photoY + 3, photoW - 6, photoH - 6);
  }

  const infoX = 290;
  const infoMaxW = CARD_H_W - infoX - 48;
  let infoY = 175;
  ctx.fillStyle = primaryDark;
  ctx.font = "bold 18px Arial, sans-serif";
  ctx.fillText("DATA PESERTA", infoX, infoY);
  infoY += 36;
  infoY = drawInfoRow(ctx, infoX, infoY, "Nama", nama, infoMaxW);
  infoY = drawInfoRow(ctx, infoX, infoY, "NIK", nik, infoMaxW);
  ctx.fillStyle = "#6b7280";
  ctx.font = "600 16px Arial, sans-serif";
  ctx.fillText("STATUS", infoX, infoY);
  ctx.fillStyle = primaryColor;
  ctx.font = "bold 22px Arial, sans-serif";
  ctx.fillText(status || "CALON PESERTA", infoX, infoY + 34);
  infoY += 62;
  infoY = drawInfoRow(
    ctx,
    infoX,
    infoY,
    "Nomor Register",
    resolveNoRegister(status, noRegister),
    infoMaxW,
  );
  if (kodeReferral) infoY = drawInfoRow(ctx, infoX, infoY, "Kode Referral", kodeReferral, infoMaxW);
  const lokasi = [kabupatenNama, provinsiNama].filter(Boolean).join(", ");
  if (lokasi) infoY = drawInfoRow(ctx, infoX, infoY, "Domisili", lokasi, infoMaxW);

  const footerY = CARD_H_H - 64;
  ctx.fillStyle = "#f3f4f6";
  roundRect(ctx, 24, footerY, CARD_H_W - 48, 52, 10);
  ctx.fill();
  ctx.fillStyle = "#6b7280";
  ctx.font = "14px Arial, sans-serif";
  ctx.textAlign = "left";
  const regDisplay = resolveNoRegister(status, noRegister);
  const shortId = (idPendamping || "").replace(/-/g, "").slice(0, 12).toUpperCase();
  ctx.fillText(
    regDisplay !== "-" ? `Reg. ${regDisplay}` : `Reg. ${shortId || "-"}`,
    44,
    footerY + 32,
  );
  ctx.textAlign = "right";
  ctx.fillText(
    `Dicetak ${new Date().toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })}`,
    CARD_H_W - 44,
    footerY + 32,
  );
  ctx.fillStyle = primaryColor;
  roundRect(ctx, 24, CARD_H_H - 18, CARD_H_W - 48, 6, 3);
  ctx.fill();

  return canvas.toDataURL("image/png", 1.0);
}

/** Kartu portrait — untuk gantungan leher (54 × 86 mm). */
async function generateKartuVertical(opts) {
  const {
    nama = "",
    nik = "",
    status = "CALON PESERTA",
    email = "",
    noHp = "",
    tglLahir = "",
    kodeReferral = "",
    noRegister = "",
    fotoUrl = "",
    logoUrl = "",
    orgName = "LP3H",
    orgAlamat = "",
    orgTelepon = "",
    primaryColor = "#10b981",
    primaryDark = "#047857",
    primaryLight = "#d1fae5",
  } = opts;

  const kartuTitle = getKartuTitle(status);

  const canvas = document.createElement("canvas");
  canvas.width = CARD_V_W;
  canvas.height = CARD_V_H;
  const ctx = canvas.getContext("2d");
  const pad = 16;
  const innerW = CARD_V_W - pad * 2;

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, CARD_V_W, CARD_V_H);
  ctx.shadowColor = "rgba(0,0,0,0.15)";
  ctx.shadowBlur = 20;
  ctx.shadowOffsetY = 6;
  roundRect(ctx, 8, 8, CARD_V_W - 16, CARD_V_H - 16, 22);
  ctx.fillStyle = "#ffffff";
  ctx.fill();
  ctx.shadowColor = "transparent";

  ctx.save();
  roundRect(ctx, pad, pad, innerW, CARD_V_H - pad * 2, 18);
  ctx.clip();

  drawVerticalThemeGradient(
    ctx,
    CARD_V_W,
    CARD_V_H,
    primaryColor,
    primaryDark,
    primaryLight,
  );

  drawLanyardSlots(ctx, CARD_V_W / 2, pad + 6);

  const centerX = CARD_V_W / 2;
  const textMaxW = innerW - 48;
  let headerY = pad + 28;

  const logoH = await drawLogoCentered(ctx, logoUrl, centerX, headerY, 52, 120);
  headerY += logoH > 0 ? logoH + 10 : 4;

  ctx.textAlign = "center";
  ctx.textBaseline = "top";

  ctx.font = "bold 14px Arial, sans-serif";
  const nameLines = wrapText(ctx, orgName || "LP3H", textMaxW).slice(0, 2);

  ctx.font = "10px Arial, sans-serif";
  const alamatLines = orgAlamat
    ? wrapText(ctx, orgAlamat, textMaxW).slice(0, 2)
    : [];
  const teleponText = orgTelepon ? `Telp: ${orgTelepon}` : "";
  const teleponLines = teleponText ? wrapText(ctx, teleponText, textMaxW).slice(0, 1) : [];

  ctx.fillStyle = "#ffffff";
  ctx.font = "bold 14px Arial, sans-serif";
  headerY = drawCenteredLines(ctx, nameLines, centerX, headerY, 16, 2);

  if (alamatLines.length) {
    ctx.fillStyle = "rgba(255,255,255,0.9)";
    ctx.font = "10px Arial, sans-serif";
    headerY = drawCenteredLines(ctx, alamatLines, centerX, headerY, 13, 2);
  }

  if (teleponLines.length) {
    ctx.fillStyle = "rgba(255,255,255,0.9)";
    ctx.font = "10px Arial, sans-serif";
    headerY = drawCenteredLines(ctx, teleponLines, centerX, headerY, 13, 1);
  }

  ctx.fillStyle = "rgba(255,255,255,0.9)";
  ctx.font = "bold 9px Arial, sans-serif";
  const titleLines = wrapText(ctx, kartuTitle, textMaxW).slice(0, 2);
  headerY = drawCenteredLines(ctx, titleLines, centerX, headerY + 6, 11, 2);

  const photoR = 96;
  const photoCy = Math.max(headerY + photoR + 18, 288);
  await drawCircularPhoto(ctx, centerX, photoCy, photoR, fotoUrl);

  const nameY = photoCy + photoR + 36;
  ctx.fillStyle = "#ffffff";
  ctx.font = "bold 26px Arial, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  ctx.fillText(truncateText(ctx, (nama || "-").toUpperCase(), innerW - 24), centerX, nameY);

  ctx.fillStyle = "rgba(255,255,255,0.92)";
  ctx.font = "600 15px Arial, sans-serif";
  ctx.fillText(status || "CALON PESERTA", centerX, nameY + 28);

  const detailsTop = nameY + 52;
  ctx.fillStyle = "#ffffff";
  roundRect(ctx, pad, detailsTop, innerW, CARD_V_H - detailsTop - pad, 14);
  ctx.fill();

  let rowY = detailsTop + 12;
  const rowX = pad + 20;
  const rowW = innerW - 40;
  rowY = drawVerticalDetailRow(ctx, rowX, rowY, rowW, "NIK", nik);
  rowY = drawVerticalDetailRow(
    ctx,
    rowX,
    rowY,
    rowW,
    "Nomor Register",
    resolveNoRegister(status, noRegister),
  );
  rowY = drawVerticalDetailRow(ctx, rowX, rowY, rowW, "Tgl Lahir", formatTgl(tglLahir));
  rowY = drawVerticalDetailRow(ctx, rowX, rowY, rowW, "Email", email);
  rowY = drawVerticalDetailRow(ctx, rowX, rowY, rowW, "No. HP", noHp);
  if (kodeReferral) {
    rowY = drawVerticalDetailRow(ctx, rowX, rowY, rowW, "Kode Referral", kodeReferral);
  }

  if (noHp) {
    const qrSize = 108;
    const qrY = CARD_V_H - pad - qrSize - 12;
    await drawQrCode(ctx, noHp, CARD_V_W / 2, qrY, qrSize);
  }

  ctx.restore();

  ctx.strokeStyle = mixColor(primaryColor, 0.2);
  ctx.lineWidth = 2;
  roundRect(ctx, pad, pad, innerW, CARD_V_H - pad * 2, 18);
  ctx.stroke();

  return canvas.toDataURL("image/png", 1.0);
}

/**
 * @param {object} opts
 * @param {'horizontal'|'vertical'} format
 * @returns {Promise<string>} data URL PNG
 */
export async function generateKartuPengenalImage(opts, format = KARTU_FORMAT.HORIZONTAL) {
  if (format === KARTU_FORMAT.VERTICAL) {
    return generateKartuVertical(opts);
  }
  return generateKartuHorizontal(opts);
}

export function getKartuFormatLabel(format) {
  return format === KARTU_FORMAT.VERTICAL
    ? "Gantungan Leher (54 × 86 mm)"
    : "Landscape (85,6 × 54 mm)";
}

export function downloadDataUrl(dataUrl, filename) {
  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = filename;
  link.click();
}

export function printDataUrl(dataUrl, title = "Kartu Pengenal", format = KARTU_FORMAT.HORIZONTAL) {
  const win = window.open("", "_blank");
  if (!win) {
    throw new Error("Popup diblokir browser. Izinkan popup untuk mencetak.");
  }
  const vertical = format === KARTU_FORMAT.VERTICAL;
  const sizeW = vertical ? "54mm" : "85.6mm";
  const sizeH = vertical ? "86mm" : "53.98mm";
  win.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>${title}</title>
        <style>
          body { margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #fff; }
          img { width: ${sizeW}; height: ${sizeH}; object-fit: contain; }
          @page { size: ${sizeW} ${sizeH}; margin: 0; }
          @media print { body { margin: 0; } img { width: ${sizeW}; height: ${sizeH}; } }
        </style>
      </head>
      <body>
        <img src="${dataUrl}" alt="Kartu Pengenal" onload="window.print();" />
      </body>
    </html>
  `);
  win.document.close();
}
